import { logError } from '@/utils/logger';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretLeft, CaretRight, CalendarBlank, MagnifyingGlass, X, MapPin, User, Clock } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const SHIFT_CONFIG = {
  day: { label: 'Jour', color: 'bg-[#F59E0B]', badge: 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30' },
  night: { label: 'Nuit', color: 'bg-[#8B5CF6]', badge: 'bg-[#8B5CF6]/20 text-[#8B5CF6] border-[#8B5CF6]/30' },
  rotating: { label: 'Rotation', color: 'bg-[#2563EB]', badge: 'bg-[#2563EB]/20 text-[#2563EB] border-[#2563EB]/30' },
};

const Calendar = () => {
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState([]);
  const [agents, setAgents] = useState([]);
  const [clients, setClients] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedZone, setSelectedZone] = useState('all');
  const [userPermissions, setUserPermissions] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAgent, setFilterAgent] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [filterShift, setFilterShift] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');

  const fetchData = useCallback(async () => {
    try {
      const zoneParam = selectedZone !== 'all' ? `?zone_id=${selectedZone}` : '';
      const [assignmentsRes, agentsRes, clientsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assignments${zoneParam}`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agents${zoneParam}`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients${zoneParam}`, { credentials: 'include' }),
      ]);
      if (assignmentsRes.ok) setAssignments(await assignmentsRes.json());
      if (agentsRes.ok) setAgents(await agentsRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (error) { logError('Fetch:', error); toast.error('Erreur de chargement'); }
  }, [selectedZone]);

  const fetchInitialData = useCallback(async () => {
    try {
      const [userRes, zonesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/zones`, { credentials: 'include' }),
      ]);
      if (userRes.ok) {
        const u = await userRes.json();
        setUserPermissions({...u.permissions, effective: u.effective_permissions || {}});
        if (!u.permissions.can_view_all_zones && u.zone_id) setSelectedZone(u.zone_id);
      }
      if (zonesRes.ok) setZones(await zonesRes.json());
      await fetchData();
    } catch (error) { logError('Init:', error); }
    finally { setLoading(false); }
  }, [fetchData]);

  useEffect(() => { fetchInitialData(); }, [fetchInitialData]);
  useEffect(() => { if (zones.length > 0) fetchData(); }, [zones.length, fetchData]);

  // Helpers
  const getAgent = (id) => agents.find(a => a.agent_id === id);
  const getAgentName = (id) => getAgent(id)?.name || id;
  const getClientName = (id) => clients.find(c => c.client_id === id)?.name || id;

  // Filtered assignments
  const filteredAssignments = useMemo(() => {
    return assignments.filter(a => {
      if (filterStatus !== 'all' && a.status !== filterStatus) return false;
      if (filterAgent !== 'all' && a.agent_id !== filterAgent) return false;
      if (filterClient !== 'all' && a.client_id !== filterClient) return false;
      if (filterShift !== 'all' && a.shift !== filterShift) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const match = getAgentName(a.agent_id).toLowerCase().includes(q) ||
          getClientName(a.client_id).toLowerCase().includes(q) ||
          (a.site_name || '').toLowerCase().includes(q);
        if (!match) return false;
      }
      return true;
    });
  }, [assignments, filterStatus, filterAgent, filterClient, filterShift, searchQuery, agents, clients]);

  // Calendar
  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

  const calendarDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }, [daysInMonth, firstDayOfMonth]);

  const getAssignmentsForDay = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return filteredAssignments.filter(a => {
      const start = new Date(a.start_date).toISOString().split('T')[0];
      const end = a.end_date ? new Date(a.end_date).toISOString().split('T')[0] : null;
      if (end) return start <= dateStr && dateStr <= end;
      return a.status === 'active' ? start <= dateStr : start === dateStr;
    });
  };

  const today = new Date();
  const isToday = (day) => day && today.getDate() === day && today.getMonth() === currentDate.getMonth() && today.getFullYear() === currentDate.getFullYear();

  // Stats
  const activeFiltered = filteredAssignments.filter(a => a.status === 'active').length;
  const uniqueAgents = new Set(filteredAssignments.map(a => a.agent_id)).size;
  const uniqueClients = new Set(filteredAssignments.map(a => a.client_id)).size;

  // Selected day detail
  const dayDetail = selectedDay ? getAssignmentsForDay(selectedDay) : [];
  const hasFilters = searchQuery || filterAgent !== 'all' || filterClient !== 'all' || filterShift !== 'all' || filterStatus !== 'active';

  if (loading) return <div className="text-[#94A3B8]">{t('common.loading')}</div>;

  return (
    <div className="space-y-5" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight">Calendrier</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">Planning des affectations</p>
        </div>
        {userPermissions?.can_view_all_zones && (
          <Select value={selectedZone} onValueChange={setSelectedZone}>
            <SelectTrigger className="w-[160px] bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0F172A] border-[#1E293B]">
              <SelectItem value="all" className="text-[#F8FAFC]">Toutes zones</SelectItem>
              {zones.map(z => <SelectItem key={z.zone_id} value={z.zone_id} className="text-[#F8FAFC]">{z.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Filters */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Rechercher agent, client, site..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] pl-9" />
          </div>
          <Select value={filterAgent} onValueChange={setFilterAgent}>
            <SelectTrigger className="w-[170px] bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0F172A] border-[#1E293B]">
              <SelectItem value="all" className="text-[#F8FAFC]">Tous les agents</SelectItem>
              {agents.map(a => <SelectItem key={a.agent_id} value={a.agent_id} className="text-[#F8FAFC]">{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="w-[170px] bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0F172A] border-[#1E293B]">
              <SelectItem value="all" className="text-[#F8FAFC]">Tous les clients</SelectItem>
              {clients.map(c => <SelectItem key={c.client_id} value={c.client_id} className="text-[#F8FAFC]">{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterShift} onValueChange={setFilterShift}>
            <SelectTrigger className="w-[140px] bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0F172A] border-[#1E293B]">
              <SelectItem value="all" className="text-[#F8FAFC]">Tous postes</SelectItem>
              <SelectItem value="day" className="text-[#F8FAFC]">Jour</SelectItem>
              <SelectItem value="night" className="text-[#F8FAFC]">Nuit</SelectItem>
              <SelectItem value="rotating" className="text-[#F8FAFC]">Rotation</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px] bg-[#1E293B] border-[#334155] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0F172A] border-[#1E293B]">
              <SelectItem value="all" className="text-[#F8FAFC]">Tous statuts</SelectItem>
              <SelectItem value="active" className="text-[#F8FAFC]">Actives</SelectItem>
              <SelectItem value="completed" className="text-[#F8FAFC]">Terminées</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {hasFilters && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#94A3B8]">{filteredAssignments.length} affectation(s) — {uniqueAgents} agent(s) — {uniqueClients} client(s)</span>
            <button onClick={() => { setSearchQuery(''); setFilterAgent('all'); setFilterClient('all'); setFilterShift('all'); setFilterStatus('active'); }} className="text-xs text-[#2563EB] hover:underline">Réinitialiser</button>
          </div>
        )}
      </div>

      {/* Stats mini */}
      <div className="flex flex-wrap gap-3">
        {Object.entries(SHIFT_CONFIG).map(([key, cfg]) => {
          const count = filteredAssignments.filter(a => a.shift === key && a.status === 'active').length;
          return (
            <div key={key} className="flex items-center gap-2 bg-[#0F172A] border border-[#1E293B] rounded-md px-3 py-2">
              <div className={`h-2.5 w-2.5 rounded-full ${cfg.color}`} />
              <span className="text-xs text-[#94A3B8]">{cfg.label}</span>
              <span className="text-xs font-bold text-[#F8FAFC]">{count}</span>
            </div>
          );
        })}
        <div className="flex items-center gap-2 bg-[#0F172A] border border-[#1E293B] rounded-md px-3 py-2">
          <span className="text-xs text-[#94A3B8]">Total actifs</span>
          <span className="text-xs font-bold text-[#10B981]">{activeFiltered}</span>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-5">
        {/* Calendar Grid */}
        <div className="flex-1 bg-[#0F172A] border border-[#1E293B] rounded-md overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E293B]">
            <Button variant="ghost" size="sm" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))} className="text-[#94A3B8] hover:text-white hover:bg-[#1E293B]">
              <CaretLeft size={20} />
            </Button>
            <div className="text-center">
              <h2 className="text-lg font-bold text-[#F8FAFC]">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))} className="text-[#94A3B8] hover:text-white hover:bg-[#1E293B]">
              <CaretRight size={20} />
            </Button>
          </div>

          <div className="grid grid-cols-7 border-b border-[#1E293B]">
            {dayNames.map(d => <div key={d} className="p-2 text-center text-xs font-medium text-[#94A3B8] bg-[#1E293B]/30">{d}</div>)}
          </div>

          <div className="grid grid-cols-7">
            {calendarDays.map((day, idx) => {
              const dayAss = day ? getAssignmentsForDay(day) : [];
              const isSelected = day === selectedDay;
              return (
                <div
                  key={day != null ? `d-${day}` : `e-${idx}`}
                  onClick={() => day && setSelectedDay(day === selectedDay ? null : day)}
                  className={`min-h-[90px] sm:min-h-[110px] p-1.5 border-b border-r border-[#1E293B] cursor-pointer transition-colors
                    ${!day ? 'bg-[#0F172A]/50' : 'hover:bg-[#1E293B]/30'}
                    ${isToday(day) ? 'bg-[#2563EB]/10' : ''}
                    ${isSelected ? 'ring-2 ring-inset ring-[#2563EB]' : ''}
                  `}
                >
                  {day && (
                    <>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-medium ${isToday(day) ? 'text-[#2563EB] font-bold' : 'text-[#F8FAFC]'}`}>{day}</span>
                        {dayAss.length > 0 && <span className="text-[10px] text-[#94A3B8] bg-[#1E293B] rounded-full px-1.5">{dayAss.length}</span>}
                      </div>
                      <div className="space-y-0.5">
                        {dayAss.slice(0, 3).map(a => (
                          <div key={a.assignment_id} className={`text-[10px] px-1 py-0.5 rounded truncate ${SHIFT_CONFIG[a.shift]?.color || 'bg-[#94A3B8]'} text-white`} title={`${getAgentName(a.agent_id)} - ${a.site_name}`}>
                            {getAgentName(a.agent_id).split(' ')[0]}
                          </div>
                        ))}
                        {dayAss.length > 3 && <div className="text-[10px] text-[#94A3B8]">+{dayAss.length - 3}</div>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Day Detail Panel */}
        <div className="lg:w-80 bg-[#0F172A] border border-[#1E293B] rounded-md overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-[#1E293B] flex items-center justify-between">
            <h3 className="text-sm font-bold text-[#F8FAFC]">
              {selectedDay ? `${selectedDay} ${monthNames[currentDate.getMonth()]}` : 'Détail du jour'}
            </h3>
            {selectedDay && (
              <button onClick={() => setSelectedDay(null)} className="text-[#94A3B8] hover:text-white"><X size={16} /></button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {!selectedDay ? (
              <div className="flex flex-col items-center justify-center h-full text-[#94A3B8] py-12">
                <CalendarBlank size={36} className="mb-3 opacity-40" />
                <p className="text-sm text-center">Cliquez sur un jour pour voir le détail</p>
              </div>
            ) : dayDetail.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-[#94A3B8]">
                <CalendarBlank size={36} className="mb-3 opacity-40" />
                <p className="text-sm">Aucune affectation ce jour</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {dayDetail.map(a => {
                  const agent = getAgent(a.agent_id);
                  const shift = SHIFT_CONFIG[a.shift] || SHIFT_CONFIG.day;
                  return (
                    <div key={a.assignment_id} className="bg-[#1E293B]/50 border border-[#334155]/50 rounded-lg p-3 space-y-2">
                      <div className="flex items-center gap-2.5">
                        {agent?.photo_url ? (
                          <img src={agent.photo_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <div className={`h-8 w-8 rounded-full ${shift.color} flex items-center justify-center text-white text-xs font-bold`}>
                            {getAgentName(a.agent_id).charAt(0)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#F8FAFC] truncate">{getAgentName(a.agent_id)}</p>
                          <Badge className={`${shift.badge} border text-[10px]`}>{shift.label}</Badge>
                        </div>
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="flex items-center gap-1.5 text-[#94A3B8]">
                          <User size={12} /> <span>{getClientName(a.client_id)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[#94A3B8]">
                          <MapPin size={12} /> <span>{a.site_name || 'Non précisé'}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[#94A3B8]">
                          <Clock size={12} /> <span>Depuis {new Date(a.start_date).toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
